import type { ReactElement } from 'react'

import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { ErrorState } from '../ErrorState/ErrorState'
import { Select } from '../Select/Select'
import type { SelectOption } from '../Select/Select'
import { Spinner } from '../Spinner/Spinner'
import styles from './machinePicker.module.scss'

type MirrorMachine = RouterOutputs['configuration']['listMachines']['data'][number]

export type MachinePickerProps = {
  label: string
  /** The chosen machine's code (`C00045`), or '' while there is none. */
  value: string
  onChange: (workstationCode: string) => void
  disabled?: boolean
}

/**
 * One option per CODE: the planner matches machines by code, so a machine
 * without one cannot be configured, and the mirror can list a code twice
 * (one machine seen under two types).
 */
const machineOptions = (machines: Array<MirrorMachine>): Array<SelectOption> => {
  const seen = new Set<string>()
  const options: Array<SelectOption> = []
  for (const machine of machines) {
    if (machine.code === null || seen.has(machine.code)) {
      continue
    }
    seen.add(machine.code)
    const name = [machine.code, machine.title].filter((part): part is string => part !== null && part !== '').join(' · ')
    options.push({ value: machine.code, label: machine.typeTitle === null ? name : `${name} (${machine.typeTitle})` })
  }
  return options
}

/**
 * Picks a machine of the mirror (`mrp.workstations_machines`, about a hundred):
 * a plain select, since the whole list fits. ⚠️ The machine dimension is derived
 * from the MOs — one nobody has used yet is not in the mirror, so it is not here
 * either.
 */
export const MachinePicker = ({ label, value, onChange, disabled = false }: MachinePickerProps): ReactElement => {
  const machinesQuery = trpc.configuration.listMachines.useQuery()

  if (machinesQuery.isPending) {
    return <Spinner label="Cargando máquinas…" />
  }
  if (machinesQuery.isError) {
    return <ErrorState error={machinesQuery.error} onRetry={() => void machinesQuery.refetch()} />
  }

  return (
    <div className={styles['machine-picker']}>
      <Select
        label={label}
        placeholder="Elige una máquina…"
        options={machineOptions(machinesQuery.data.data)}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <FreshnessBadge freshness={machinesQuery.data.freshness} />
    </div>
  )
}
